import React, { createContext, useContext, ReactNode, useEffect } from 'react';
import { useFavoritesStore } from '../stores/favoritesStore';
import { useAuth } from './AuthContext';
import type { RadioStation } from '../types/radio';
import { doc, onSnapshot } from 'firebase/firestore';
import { db } from '../lib/firebase';
import { useToast } from './ToastContext';
import { radioService } from '../api/radioService';

interface FavoritesContextType {
  favorites: RadioStation[];
  addFavorite: (station: RadioStation) => Promise<void>;
  removeFavorite: (station: RadioStation) => Promise<void>;
  isFavorite: (stationId: string) => boolean;
  clearFavorites: () => void;
}

const FavoritesContext = createContext<FavoritesContextType | undefined>(undefined);

export const FavoritesProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const { user } = useAuth();
  const { showToast } = useToast();
  const {
    favorites,
    addFavorite: addFavoriteToStore,
    removeFavorite: removeFavoriteFromStore,
    isFavorite,
    clearFavorites,
    setFavorites,
    setUserId
  } = useFavoritesStore();

  // Subscribe to user's favorites in Firestore
  useEffect(() => {
    if (!user) {
      clearFavorites();
      setUserId(null);
      return;
    }

    setUserId(user.uid);
    const userRef = doc(db, 'users', user.uid);
    const unsubscribe = onSnapshot(userRef, async (doc) => {
      if (doc.exists()) {
        const userData = doc.data();
        if (userData?.favorites && Array.isArray(userData.favorites)) {
          try {
            // Fetch full station data for each favorite
            const stationPromises = userData.favorites.map(async (stationId: string) => {
              try {
                return await radioService.getStationById(stationId);
              } catch (error) {
                console.error(`Error fetching station ${stationId}:`, error);
                return null;
              }
            });

            const stations = await Promise.all(stationPromises);
            const validStations = stations.filter(Boolean) as RadioStation[];
            setFavorites(validStations);
          } catch (error) {
            console.error('Error fetching favorite stations:', error);
            showToast('Error loading favorites', 'error');
          }
        } else {
          setFavorites([]);
        }
      }
    });

    return () => unsubscribe();
  }, [user, clearFavorites, setFavorites, showToast, setUserId]);

  const addFavorite = async (station: RadioStation) => {
    if (!user) {
      showToast('Please sign in to add favorites', 'error');
      return;
    }

    try {
      await addFavoriteToStore(station);
      showToast('Added to favorites', 'success');
    } catch (error) {
      console.error('Error adding favorite:', error);
      showToast('Failed to add to favorites', 'error');
    }
  };

  const removeFavorite = async (station: RadioStation) => {
    if (!user) {
      showToast('Please sign in to remove favorites', 'error');
      return;
    }

    try {
      await removeFavoriteFromStore(station);
      showToast('Removed from favorites', 'success');
    } catch (error) {
      console.error('Error removing favorite:', error);
      showToast('Failed to remove from favorites', 'error');
    }
  };

  return (
    <FavoritesContext.Provider
      value={{
        favorites,
        addFavorite,
        removeFavorite,
        isFavorite,
        clearFavorites,
      }}
    >
      {children}
    </FavoritesContext.Provider>
  );
};

export const useFavorites = () => {
  const context = useContext(FavoritesContext);
  if (context === undefined) {
    throw new Error('useFavorites must be used within a FavoritesProvider');
  }
  return context;
}; 